import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { ellipsis } from "polished";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";

import { VisibilityOn } from "@livechat/design-system-icons";
import {
  Avatar,
  Icon as DSIcon,
  DesignToken,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { Icon } from "components/basic";
import { IconsEnum } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import PriorityIndicator from "components/priority/PriorityIndicator";
import { StatusTag } from "components/status-tag/StatusTag";
import { UserSymbol, UserSymbolSize } from "components/user-symbol";
import { DateFormat } from "constants/date";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { Column, ColumnWidths } from "constants/tickets/columns";
import { formatTimeDistance, timeAgo } from "helpers/date";
import { shouldOpenNewWindow } from "helpers/routing";
import { getEventDate } from "helpers/tickets";
import { getAppRootWithOrigin } from "helpers/url";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { ITicket, TicketSilo } from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { IAgent, selectAgents } from "store/entities/agents";
import { selectTicketViewers } from "store/features/ticket-activity/selectors";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import TicketContextMenu from "./ticket/ticket-details/ticket-context-menu/TicketContextMenu";
import { Td, cellPaddingCss } from "./tickets-styles";

interface IProps extends RouteComponentProps {
  column: Column;
  ticket: ITicket;
  route: TicketsRoutes;
  agents: IAgent[];
  onMouseEnter?(e: React.MouseEvent): void;
  onMouseLeave?(e: React.MouseEvent): void;
  decorator?(children: React.ReactNode): React.ReactNode;
  onSpamModalRequest(type: "spam" | "unspam", selectedIds: string[]): void;
}

interface IDecoratorAttributes {
  css: SerializedStyles;
  onMouseEnter?(e: React.MouseEvent): void;
  onMouseLeave?(e: React.MouseEvent): void;
}

const Faded = styled.span`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
`;

const StyledUserSymbol = styled.div`
  margin-right: 8px;
`;

const EllipsisWrapper = styled.div`
  display: flex;
  flex: 1 1;
  overflow: hidden;
`;

const Ellipsis = styled(Text)`
  width: auto;
  min-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-wrap: normal;
`;

const AlignIndicator = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-top: -2px;
  margin-right: var(${SpacingToken.Spacing1});
`;

const EmptySubject = styled.span`
  color: ${withVar(DesignToken.ContentBasicDisabled)};
`;

const Draft = styled.span`
  color: ${withVar(DesignToken.ContentBasicNegative)};
`;

const Requester = styled.div`
  display: flex;
  align-items: center;
`;

const RequesterData = styled.div`
  display: flex;
  flex-direction: column;
`;

const RequesterName = styled(Text)`
  ${ColumnWidths.requester && ellipsis(ColumnWidths.requester - 40)};
`;

const RequesterEmail = styled(RequesterName)`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
`;

const MergedTicketsWrapper = styled.div`
  pointer-events: none;
  cursor: not-allowed;
`;

const LastMessageWrapper = styled.div`
  max-width: fit-content;
`;

const mergedTicketIconStyles = css`
  display: inline;
  position: relative;
`;

const subjectLinkStyle = css`
  text-decoration: none;
  color: inherit;
  display: block;
`;

interface IActivityStateProps {
  agents: Record<string, IAgent>;
  viewingAgentIDs: string[];
}

function mapStateToActivityProps(
  state: IStoreState,
  ownProps: { ticketID: string; ticketSilo: TicketSilo }
): IActivityStateProps {
  return {
    agents: selectAgents(state),
    viewingAgentIDs: selectTicketViewers(state, ownProps.ticketID),
  };
}

/* eslint-disable react/prefer-stateless-function */
// Disabled because we want withBreakpoints which expects ComponentType instead of React.FC
class TicketListActivityIconRaw extends React.Component<
  { ticketID: string; ticketSilo: TicketSilo } & IActivityStateProps & IWithBreakpointsProps
> {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { viewingAgentIDs, agents, isMobile, ticketSilo } = this.props;

    const viewers = viewingAgentIDs.map(id => agents[id]).filter(Boolean);
    const isRecentTicket = ticketSilo === TicketSilo.Tickets;

    return !isMobile && isRecentTicket && viewers.length ? (
      <Tooltip
        placement="bottom"
        kind="invert"
        triggerRenderer={
          <span>
            <DSIcon source={VisibilityOn} size="small" />
          </span>
        }
      >
        <Block minWidth="100%">
          {viewers.map(v => v.name).join(", ")} {plural(viewers.length, "is", "are")} viewing
        </Block>
      </Tooltip>
    ) : null;
  }
}

/* eslint-disable react-redux/connect-prefer-named-arguments */
const TicketListActivityIcon = connect(mapStateToActivityProps)(withBreakpoints(TicketListActivityIconRaw));

function getAssigneeName(assigneeId: string, agents: IAgent[]): string | null {
  const assignee = agents.find((agent: IAgent) => agent.id === assigneeId);
  if (assignee) {
    return assignee.name;
  }
  return null;
}

function getAssigneeAvatar(assigneeId: string, agents: IAgent[]): string | undefined {
  const assignee = agents.find((agent: IAgent) => agent.id === assigneeId);
  return assignee?.avatar ?? undefined;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const getMergedTicketIcon = (ticket: any) => {
  if (ticket.parentTicket) {
    return <Icon name={IconsEnum.ChildTicket} css={mergedTicketIconStyles} width="20px" height="20px" fill="none" />;
  }
  if (ticket.childTickets.length) {
    return <Icon name={IconsEnum.ParentTicket} css={mergedTicketIconStyles} width="20px" height="20px" fill="none" />;
  }
  return null;
};

function fieldFactory(
  column: Column,
  ticket: ITicket,
  agents: IAgent[],
  onSpamModalRequest: (type: "spam" | "unspam", selectedIds: string[]) => void
): { title: string; field: JSX.Element | string } {
  switch (column) {
    case Column.Requester:
      return {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
        title: null,
        field: (
          <Requester>
            <StyledUserSymbol>
              <UserSymbol
                // @ts-expect-error TS(2322)
                userId={ticket.requester.email}
                name={ticket.requester.name || ticket.requester.email}
                size={UserSymbolSize.Small}
              />
            </StyledUserSymbol>
            <RequesterData>
              <RequesterName className="fs-mask" title={ticket.requester.name}>
                {ticket.requester.name}
              </RequesterName>
              <RequesterEmail size="sm" className="fs-mask" title={ticket.requester.email}>
                {ticket.requester.email}
              </RequesterEmail>
            </RequesterData>
          </Requester>
        ),
      };

    case Column.Subject: {
      const draft = ticket.draft ? <Draft>(Draft) </Draft> : null;
      const { priority } = ticket;

      return {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
        title: null,
        field: (
          <EllipsisWrapper className="fs-mask">
            <Ellipsis>
              <AlignIndicator>
                <PriorityIndicator priority={priority} size="small" />
              </AlignIndicator>
              {draft}
              {ticket.subject || <EmptySubject>No subject</EmptySubject>}
            </Ellipsis>
            {getMergedTicketIcon(ticket)}
          </EllipsisWrapper>
        ),
      };
    }

    case Column.Assignee: {
      const assignee = ticket.assignment.agent ? getAssigneeName(ticket.assignment.agent.id, agents) : "unassigned";
      return {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
        title: null,
        field: (
          <Flex minWidth="0px" alignItems="center" justifyContent="space-between">
            <EllipsisWrapper className="fs-mask">
              <Ellipsis>{assignee}</Ellipsis>
            </EllipsisWrapper>
            <Block marginLeft="auto">
              <TicketListActivityIcon ticketID={ticket.id} ticketSilo={ticket.silo} />
            </Block>
          </Flex>
        ),
      };
    }

    case Column.LastMessageDate:
    case Column.UnrepliedSince: {
      const { lastMessageAt } = ticket;
      // @ts-expect-error TS(2322): Type '{ title: null; field: React.JSX.Element; }' ... Remove this comment to see the full error message
      return lastMessageAt
        ? {
            title: null,
            field: (
              <LastMessageWrapper>
                <Tooltip
                  kind="invert"
                  fullSpaceContent
                  transitionDelay={400}
                  placement="top"
                  css={css({ padding: "4px 6px" })}
                  triggerRenderer={() => (
                    <Text>
                      {timeAgo(lastMessageAt, new Date(), {
                        dateFormat: DateFormat.DateWithoutDayOfTheWeek,
                        useStrict: false,
                      })}
                    </Text>
                  )}
                >
                  <Text size="xs" textAlign="center">
                    {getEventDate(lastMessageAt)}
                  </Text>
                </Tooltip>
              </LastMessageWrapper>
            ),
          }
        : {
            title: null,
            field: <Faded>—</Faded>,
          };
    }

    case Column.LastActivity: {
      const lastActivityDate = ticket.updatedAt || ticket.date;
      return {
        title: getEventDate(lastActivityDate),
        field: timeAgo(lastActivityDate, new Date(), {
          dateFormat: DateFormat.DateWithoutDayOfTheWeek,
          useStrict: false,
        }),
      };
    }

    case Column.Status: {
      return {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
        title: null,
        field: <StatusTag status={ticket.status} />,
      };
    }
    case Column.Checkbox:
      return {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
        title: null,
        field: "",
      };

    case Column.Options:
      return {
        // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
        title: null,
        field: (
          <TicketContextMenu
            onSpamModalRequest={onSpamModalRequest}
            ticket={ticket}
            // @ts-expect-error TS(2322): Type '{ onSpamModalRequest: (type: "spam" | "unspa... Remove this comment to see the full error message
            modifiers={{
              offset: {
                offset: "-10px 0",
              },
            }}
          />
        ),
      };

    case Column.Mobile: {
      const assignee = ticket.assignment.agent ? getAssigneeName(ticket.assignment.agent.id, agents) : "unassigned";
      const assigneeAvatar = getAssigneeAvatar(ticket?.assignment?.agent?.id ?? "", agents);
      return {
        title: "",
        field: (
          <Flex column padding={`${withVar(SpacingToken.Spacing3)} 0`}>
            <RequesterEmail size="sm" title={ticket.requester.email} css={{ maxWidth: "unset" }}>
              {ticket.requester.email}
            </RequesterEmail>
            <Flex alignItems="flex-start">
              <PriorityIndicator priority={ticket.priority} size="small" css={{ marginTop: "3px" }} />
              <Text bold>{ticket.subject}</Text>
            </Flex>
            <Block marginTop={withVar(SpacingToken.Spacing1)} />
            <Flex>
              <StatusTag status={ticket.status} />
              <Avatar
                type={assignee !== "unassigned" && assigneeAvatar === undefined ? "text" : "image"}
                text={assignee || ""}
                size="xxsmall"
                src={assigneeAvatar}
                css={{ marginRight: withVar(SpacingToken.Spacing1), marginLeft: withVar(SpacingToken.Spacing2) }}
              />
              <EllipsisWrapper>
                <Ellipsis>{assignee}</Ellipsis>
              </EllipsisWrapper>
            </Flex>
          </Flex>
        ),
      };
    }
    case Column.MobileOptions: {
      const lastActivityDate = ticket.updatedAt || ticket.date;
      return {
        title: getEventDate(lastActivityDate),
        field: (
          <Flex column alignItems="flex-end">
            <Text
              customColor={withVar(DesignToken.ContentBasicSecondary)}
              css={{ marginRight: withVar(SpacingToken.Spacing1) }}
            >
              {formatTimeDistance(lastActivityDate, new Date())}
            </Text>
            <TicketContextMenu onSpamModalRequest={onSpamModalRequest} ticket={ticket} />
          </Flex>
        ),
      };
    }

    default:
      throw Error(`${column} column not supported`);
  }
}

class TicketField extends React.PureComponent<IProps> {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  routeToTicket = (ticket: ITicket) => (e: React.MouseEvent<HTMLElement>) => {
    const url = `/tickets/${ticket.id}`;
    if (shouldOpenNewWindow(e)) {
      window.open(`${getAppRootWithOrigin()}${url}`);
    } else {
      this.props.history.push(url);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { column, ticket, agents, decorator, onMouseEnter, onMouseLeave, onSpamModalRequest } = this.props;
    const fieldObject = fieldFactory(column, ticket, agents, onSpamModalRequest);
    const { title } = fieldObject;
    let { field } = fieldObject;

    const tdAttrs = { column, title, withPointer: true, onClick: this.routeToTicket(ticket) };
    const decoratorAttrs: IDecoratorAttributes = { css: cellPaddingCss };

    if (column === Column.Subject) {
      decoratorAttrs.onMouseEnter = onMouseEnter;
      decoratorAttrs.onMouseLeave = onMouseLeave;
      field = (
        <a
          href={`/tickets/${ticket.id}`}
          css={subjectLinkStyle}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            this.routeToTicket(ticket)(e);
          }}
        >
          {field}
        </a>
      );
    }

    if (ticket.parentTicket) {
      field = <MergedTicketsWrapper>{field}</MergedTicketsWrapper>;
    }

    if (decorator) {
      return <Td {...tdAttrs}>{decorator(<div {...decoratorAttrs}>{field}</div>)}</Td>;
    }

    return (
      <Td {...tdAttrs} {...decoratorAttrs}>
        {field}
      </Td>
    );
  }
}

export default withRouter(TicketField);
